<script setup lang="ts">
import { htmlToPlainText } from '~/composables/htmlToPlainText'
import { resolveSeoMetadata } from '~/composables/resolveSeoMetadata'

const props = defineProps(['model'])
const config = useRuntimeConfig()
const { showFooterForm } = useLayoutFlags()
showFooterForm.value = true

const seoMeta = resolveSeoMetadata(props.model)
useSeo({
  title: seoMeta.title,
  documentTitle: seoMeta.documentTitle,
  description: seoMeta.description,
  images: seoMeta.images,
  skus: props.model.product?.skus,
})

const relatedProducts = computed(() => ({
  featuredProducts: props.model?.product?.relatedProducts,
}))

// Plain text for the query param; the <h1> below still renders the title's HTML.
const sampleRequestTitle = computed(() =>
  encodeURIComponent(htmlToPlainText(props.model.product?.title)))

// Stored TrustBox embed HTML (PIM); swapped in once the GTM-loaded Trustpilot
// script is ready, unless the product always-cookieless flag is on (see nuxt.config).
const productStarWidgetHtml = computed(() =>
  props.model.product?.trustPilotProductStarWidget?.fragments?.[0]?.internalFormat || '')
const productReviewWidgetHtml = computed(() =>
  props.model.product?.trustPilotProductReviewWidget?.fragments?.[0]?.internalFormat || '')
</script>

<template>
  <div class="productPageContainer flex w-full justify-center">
    <div class="productPage w-4/5">
      <div
        v-if="model.breadcrumbLinks"
        class="mb-5"
      >
        <span
          v-for="(link, index) in model.breadcrumbLinks.value"
          :key="link.text"
          class="font-semibold text-med-dark-gray"
        >
          <NuxtLink
            class="underline"
            :to="useCmsUrl(link.href)"
          >
            {{ link.text }}
          </NuxtLink>

          <span v-if="+index < model.breadcrumbLinks.value.length - 1">
            >
          </span>
        </span>
      </div>
      <div>
        <div
          class="flex flex-col lg:flex-row border-b-2 border-info gap-10"
          :class="[
            { 'block-spacing': !model.product.relatedProducts?.length },
          ]"
        >
          <div class="min-w-0 lg:flex-2/3">
            <div>
              <h1
                v-if="model?.product?.title"
                v-html="model.product.title"
              />
            </div>
            <div
              v-if="model.product?.trustpilotReviews?.aggregate"
              class="mt-2"
            >
              <TrustpilotWidgetGate
                :widget-html="productStarWidgetHtml"
                :always-cookieless="config.public.trustpilotProductAlwaysCookieless"
              >
                <TrustpilotProductSummary
                  :data="model.product.trustpilotReviews"
                  reviews-target-id="product-reviews"
                />
              </TrustpilotWidgetGate>
            </div>
            <div class="flex flex-wrap gap-5 pt-8 sm:py-8">
              <UButton
                v-if="model.ctaButton1?.value?.href"
                class="btn no-underline w-full sm:w-62.5 justify-center"
                :to="`${useCmsUrl(model.ctaButton1.value.href)}?sample_product_requested=${sampleRequestTitle}`"
                :locale="false"
                color="warning"
                variant="solid"
                :label="model.ctaButton1.value.text"
              />
              <UButton
                v-if="model.ctaButton2?.value?.href"
                class="btn no-underline w-full sm:w-62.5 justify-center"
                :to="useCmsUrl(model.ctaButton2.value.href)"
                :locale="false"
                color="neutral"
                variant="solid"
                :label="model.ctaButton2.value.text"
              />
            </div>
            <div class="mx-auto max-w-md py-4 lg:hidden">
              <ImageCarouselBlock :images="model.product.images" />
            </div>
            <div
              v-if="model.product?.featureDescription?.fragments?.[0]?.internalFormat"
              class="border-t-2 border-med-aqua py-8"
            >
              <ProductAccordionBlock
                :init-expanded="true"
                :is-expandable="false"
                :title="model.featuresHeader.value"
                :contents="model.product.featureDescription.fragments[0].internalFormat"
                details-class="prose"
              />
            </div>
            <div
              v-if="model.product?.description?.fragments?.[0]?.internalFormat"
              class="border-t-2 border-med-aqua py-8"
            >
              <ProductAccordionBlock
                :init-expanded="false"
                :title="model.descriptionHeader.value"
                :contents="model.product?.description?.fragments[0].internalFormat"
                details-class="prose"
              />
            </div>
            <div
              v-if="model.product?.sizingDescription?.fragments?.[0]?.internalFormat"
              class="border-t-2 border-med-aqua py-8"
            >
              <ProductAccordionBlock
                :init-expanded="false"
                :title="model.sizesHeader.value"
                :contents="model.product?.sizingDescription?.fragments[0].internalFormat"
                details-class="prose"
              />
            </div>
            <div
              v-if="model.product?.urlLinks"
              class="border-t-2 border-med-aqua py-8"
            >
              <ProductAccordionBlock
                :init-expanded="false"
                :title="model.resourcesHeader.value"
                :contents="model.product?.urlLinks"
                details-class="prose"
              />
            </div>
            <div
              v-if="model.product?.trustpilotReviews?.aggregate"
              id="product-reviews"
              class="border-t-2 border-med-aqua py-8 scroll-mt-48"
            >
              <ProductAccordionBlock
                :init-expanded="true"
                :title="model.reviewsHeader.value"
              >
                <TrustpilotWidgetGate
                  :widget-html="productReviewWidgetHtml"
                  :always-cookieless="config.public.trustpilotProductAlwaysCookieless"
                >
                  <CookielessTrustpilotReviews :data="model.product.trustpilotReviews" />
                </TrustpilotWidgetGate>
              </ProductAccordionBlock>
            </div>
          </div>
          <div class="hidden lg:block lg:flex-1/3 pb-4">
            <ImageGalleryBlock :images="model.product.images" />
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="related-products-container container-fluid my-16">
    <div class="page-block-area w-full">
      <div
        v-if="relatedProducts?.featuredProducts.length"
      >
        <div class="font-bold text-2xl pb-8">
          {{ model.relatedProductsHeader.value }}
        </div>
        <div class="flex flex-row">
          <ProductListingBlock
            :model="relatedProducts"
            class="plb--related"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@use '@/assets/css/pages/product-page.scss';
</style>
